import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';
import {FormGroup, FormBuilder} from '@angular/forms';
import {ApiService} from '../services/api.service';
import {PromoteSet, PromoteFilter} from '../model/promote-set';
import {DeploymentSetShortStatus} from '../model/deployment-set';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatTableDataSource} from '@angular/material/table';
import {SelectionModel} from '@angular/cdk/collections';
import {MatSort} from '@angular/material/sort';
import {Utils} from '../common/utils';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorHelper} from '../common/ErrorHelper';
import {NotifierService} from 'angular-notifier';

@Component({
  selector: 'app-promote-dynamic',
  templateUrl: './promote-dynamic.component.html',
  styleUrls: ['./promote-dynamic.component.scss']
})
export class PromoteDynamicComponent implements AfterViewInit {

  constructor(
    private apiService: ApiService,
    private notifierService: NotifierService,
    private formBuilder: FormBuilder
  ) {
    this.dataSource = new MatTableDataSource<DeploymentSetShortStatus>(this.promoteElements);
    this.selection = new SelectionModel<DeploymentSetShortStatus>(true, []);
    this.loadDataAsync();
    this.createAndLoadFormWithInitialValues();
  }

  dataSource: MatTableDataSource<DeploymentSetShortStatus>;
  promoteElements: DeploymentSetShortStatus[];
  selection: SelectionModel<DeploymentSetShortStatus>;
  filteredOptions: Observable<string[]>;
  promoteForm: FormGroup;
  areaPairs = [['embargo', 'preprod'], ['embargo', 'prod'], ['preprod', 'prod']];
  areas: string[] = ['Embargo -> Pre-Production', 'Embargo -> Production', 'Pre-Production -> Production'];
  promoteSet: PromoteSet;
  displayedColumns: string[] = ['disId', 'lastModifiedDate', 'lastPreprodPromoteDate', 'lastProdPromoteDate', 'promoteAll', 'promoteMeta'];

  @ViewChild(MatSort) sortMeta: MatSort;

  private static disIdPredicate(disId: string, filter: string): boolean {
    return disId.toLowerCase().indexOf(filter.toLowerCase()) > -1;
  }

  private static filterPredicate(data: DeploymentSetShortStatus, filter: string): boolean {
    // status filter
    const wasPreprod = data.lastPreprodPromoteDate != null && data.lastModifiedDate < data.lastPreprodPromoteDate;
    const wasProd = data.lastProdPromoteDate != null && data.lastModifiedDate < data.lastProdPromoteDate;
    const wasPromoted = wasPreprod || wasProd;
    const promoteFilter: PromoteFilter = JSON.parse(filter);
    let statusFilter = promoteFilter.notPromoted && !wasPromoted;
    statusFilter = statusFilter || promoteFilter.toPreProd && wasPreprod;
    statusFilter = statusFilter || promoteFilter.toProd && wasProd;
    // disId filter
    const disIdFilter = promoteFilter.disId === '' ? true : PromoteDynamicComponent.disIdPredicate(data.disId, promoteFilter.disId);
    // date filter
    // AAARGH, date fields are defined as date but come in as strings
    // we convert strange date to proper date (createProperDate) , use date part of ISO format for comparison (getISODay)
    const lastModifiedDay = Utils.getISODay(Utils.createProperDate(data.lastModifiedDate));
    let dateFilter = true;
    if (promoteFilter.from != null) {
      // date from widget is one day off, so we add one day (addDays)
      const fromDate = Utils.addDays(Utils.createProperDate(promoteFilter.from), 1);
      const fromDay = Utils.getISODay(fromDate);
      dateFilter = dateFilter && fromDay <= lastModifiedDay;
    }
    if (promoteFilter.to != null) {
      // date from widget is one day off, so we add one day (addDays)
      const toDate = Utils.addDays(Utils.createProperDate(promoteFilter.to), 1);
      const toDay = Utils.getISODay(toDate);
      dateFilter = dateFilter && lastModifiedDay <= toDay;
    }
    return statusFilter && disIdFilter && dateFilter;
  }

  ngAfterViewInit(): void {
  }

  async loadDataAsync(): Promise<any> {
    try {
      await this.loadData();
    } catch (error) {
      console.log(error);
    }
  }

  loadData(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getAllShortStatus().toPromise().then(
        (data) => {
          this.promoteElements = data;
          this.dataSource = new MatTableDataSource<DeploymentSetShortStatus>(this.promoteElements);
          this.dataSource.sort = this.sortMeta;
          this.dataSource.filterPredicate = (shortStatus: DeploymentSetShortStatus, filter: string) =>
            PromoteDynamicComponent.filterPredicate(shortStatus, filter);
          this.selection = new SelectionModel<DeploymentSetShortStatus>(true, []);
          console.log('Fetched the promote elements');
          resolve(true);
        },
        (error) => {
          const errorMessage = ErrorHelper.disectError('get status', error);
          this.notifierService.notify('error', errorMessage);
          resolve(false);
        });
    });
  }

  private createAndLoadFormWithInitialValues(): void {
    this.promoteSet = new PromoteSet();
    this.resetPromoteSet();
    this.resetFilter();

    this.promoteForm = this.formBuilder.group({
      area: this.promoteSet.area,
      notPromoted: this.promoteSet.notPromoted,
      toPreProd: this.promoteSet.toPreProd,
      toProd: this.promoteSet.toProd,
      disId: this.promoteSet.disId,
      from: this.promoteSet.from,
      to: this.promoteSet.to
    });

    this.promoteForm.valueChanges
      .subscribe(form => {
        const filter = new PromoteFilter();
        filter.notPromoted = form.notPromoted;
        filter.toPreProd = form.toPreProd;
        filter.toProd = form.toProd;
        filter.disId = form.disId;
        filter.from = form.from;
        filter.to = form.to;
        this.dataSource.filter = JSON.stringify(filter);
      });

    this.filteredOptions = this.promoteForm.controls.disId.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        map(term => this.promoteElements.map(pE => pE.disId).filter(
          disId => PromoteDynamicComponent.disIdPredicate(disId, term)
        ))
      );
  }

  revert(): void {
    this.resetPromoteSet();
    this.resetFilter();
    this.resetFormGroup(this.promoteForm);
    this.selection.clear();
  }

  private resetFormGroup(formGroup: FormGroup): void {
    formGroup.controls.area.setValue(this.promoteSet.area);
    formGroup.controls.notPromoted.setValue(this.promoteSet.notPromoted);
    formGroup.controls.toPreProd.setValue(this.promoteSet.toPreProd);
    formGroup.controls.toProd.setValue(this.promoteSet.toProd);
    formGroup.controls.disId.setValue(this.promoteSet.disId);
    formGroup.controls.from.setValue(this.promoteSet.from);
    formGroup.controls.to.setValue(this.promoteSet.to);
  }

  private resetPromoteSet(): void {
    this.promoteSet.area = this.areas[0];
    this.promoteSet.notPromoted = true;
    this.promoteSet.toPreProd = true;
    this.promoteSet.toProd = true;
    this.promoteSet.disId = '';
    this.promoteSet.from = null;
    this.promoteSet.to = null;
  }

  private resetFilter(): void {
    const filter = new PromoteFilter();
    filter.notPromoted = this.promoteSet.notPromoted;
    filter.toPreProd = this.promoteSet.toPreProd;
    filter.toProd = this.promoteSet.toProd = true;
    filter.disId = this.promoteSet.disId;
    filter.from = this.promoteSet.from = null;
    filter.to = this.promoteSet.to;
    this.dataSource.filter = JSON.stringify(filter);
  }

  selectedItem(event: MatAutocompleteSelectedEvent): void {
    console.log(event.option.value);
    this.promoteSet.disId = event.option.value;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(): void {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: DeploymentSetShortStatus): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.disId + 1}`;
  }

  promoteAll(disId: string): void {
    const area: string = this.promoteForm.controls.area.value;
    let source = '';
    let target = '';
    for (const index in this.areas) {
      if (this.areas[index] === area) {
        source = this.areaPairs[index][0];
        target = this.areaPairs[index][1];
      }
    }

    this.apiService.promote(disId, source, target)
      .subscribe((success) => {
          console.log(`success ${area}: ${disId} from ${source} to ${target}`);
        },
        (err: HttpErrorResponse) => {
          const errorMessage = ErrorHelper.disectError(`promote ${disId} from ${source} to ${target}`, err);
          this.notifierService.notify('error', errorMessage);
        },
        () => {
          console.log(`complete ${area}: ${disId} from ${source} to ${target}`);
          this.notifierService.notify('success', `Promotion request for ${disId} created`);
        });
  }

  promoteMeta(disId: string): void {
    const area: string = this.promoteForm.controls.area.value;
    let source = '';
    let target = '';
    for (const index in this.areas) {
      if (this.areas[index] === area) {
        source = this.areaPairs[index][0];
        target = this.areaPairs[index][1];
      }
    }

    this.apiService.promoteMeta(disId, source, target)
      .subscribe((success) => {
          console.log(`success promoteMeta ${area}: ${disId} from ${source} to ${target}`);
        },
        (err: HttpErrorResponse) => {
          const errorMessage = ErrorHelper.disectError(`promote metadata for ${disId} from ${source} to ${target}`, err);
          this.notifierService.notify('error', errorMessage);
        },
        () => {
          console.log(`complete promoteMeta ${area}: ${disId} from ${source} to ${target}`);
          this.notifierService.notify('success', `Metadata Promotion for ${disId} done`);
        });
  }

}

